import {HttpClient, HttpParams} from "@angular/common/http";
import {inject, Injectable} from "@angular/core";
import {DateRange} from "@angular/material/datepicker";
import {Category} from "@core/model/retailer/application-form.model";
import {environment} from "@env/environment";
import {toIsoDateEndOfDay, toIsoDateStartOfDay} from "@ui-kit/framework/util/static/date-utils";
import {toRequestParams} from "@ui-kit/framework/util/static/http-utils";
import {cloneDeep} from "lodash";
import {Observable} from "rxjs";
import {GenericChart} from "src/app/shared/model/generic-chart.interface";
import {
  CreateSurveyRequest,
  DuplicateSurveyRequest,
  QuestionStatsGroup,
  Survey,
  SurveyStatisticsRequest,
  SurveySubmission,
  UpdateSurveyRequest
} from "./surveys.model";

@Injectable({
  providedIn: 'root'
})
export class SurveyService {

  private _http = inject(HttpClient);
  private _apiUrl = environment.apiRetailersHost + `/console/offering/district/pipeline/survey`;
  private _surveyOverviewChartEndpoint = '/statistics/responses';


  getAllSurveys(dateRange: DateRange<Date>): Observable<Survey[]> {
    const requestParams = this.buildDateRangeParams(dateRange);
    return this._http.get<Survey[]>(this._apiUrl, {params: requestParams});
  }

  getOverviewChartData(dateRange: DateRange<Date>): Observable<GenericChart> {
    const requestParams = this.buildDateRangeParams(dateRange);
    return this._http.get<GenericChart>(this._apiUrl + this._surveyOverviewChartEndpoint, {params: requestParams});
  }

  createSurvey(survey: CreateSurveyRequest): Observable<Survey> {
    return this._http.post<Survey>(this._apiUrl, survey);
  }

  duplicateSurvey(surveyValuesOverride: DuplicateSurveyRequest): Observable<Survey> {
    return this._http.post<Survey>(`${this._apiUrl}/${surveyValuesOverride.templateUuid}/duplicate`, surveyValuesOverride);
  }

  getOneSurvey(surveyUuid: string): Observable<Survey> {
    return this._http.get<Survey>(`${this._apiUrl}/${surveyUuid}`);
  }

  updateSurvey(survey: UpdateSurveyRequest): Observable<Survey> {
    if(!survey.startDate){
      throw new Error('Survey start date is required');
    }

    survey = {
      ...survey,
      startDate: toIsoDateStartOfDay(new Date(survey.startDate!))!,
      endDate: toIsoDateEndOfDay(survey.endDate ?? null)
    }
    return this._http.post<Survey>(`${this._apiUrl}/${survey.uuid}`, survey);
  }

  deleteSurvey(templateUuid: string): Observable<void> {
    return this._http.delete<void>(`${this._apiUrl}/${templateUuid}`);
  }

  // submissions

  getPipelineChartData(templateUuid: string, request: SurveyStatisticsRequest): Observable<GenericChart> {
    let requestParams = this.buildDateRangeParams(new DateRange(request.fromDate, request.toDate));
    requestParams = requestParams.append('formTemplateCode', request.formTemplateCode);
    return this._http.get<GenericChart>(`${this._apiUrl}/${templateUuid}/statistics/pipeline`, {params: requestParams});
  }

  getAllSubmissionsForSurvey(templateUuid: string, request: SurveyStatisticsRequest): Observable<SurveySubmission[]> {
    let requestParams = this.buildDateRangeParams(new DateRange(request.fromDate, request.toDate));
    requestParams = requestParams.append('formTemplateCode', request.formTemplateCode);
    return this._http.get<SurveySubmission[]>(`${this._apiUrl}/${templateUuid}/submissions`, {params: requestParams});
  }

  getOneSubmissionForSurvey(templateUuid: string, submissionUuid: string): Observable<SurveySubmission> {
    return this._http.get<SurveySubmission>(`${this._apiUrl}/${templateUuid}/submissions/${submissionUuid}`);
  }

  getSubmissionStatistics(templateUuid: string, groupCode: string): Observable<QuestionStatsGroup[]> {
    const requestParams = new HttpParams().set('groupCode', groupCode);
    return this._http.get<QuestionStatsGroup[]>(`${this._apiUrl}/${templateUuid}/statistics`, {params: requestParams});
  }

  public mapToUpdateSurveyRequest(survey: Survey, categories?: Category[]): UpdateSurveyRequest {
    return {
      ...survey ? cloneDeep(survey) : {},
      uuid: survey.uuid!,
      offeringUuid: survey.offeringUuid!,
      name: survey.name!,
      displayTitle: survey.displayTitle!,
      audience: survey.audience!,
      status: survey.status!,
      startDate: survey.startDate!,
      endDate: survey.endDate,
      allowMultiSubmission: survey.allowMultiSubmission!,
      payload: categories ?? survey.payload ?? [],
    };
  }


  private buildDateRangeParams(dateRange: DateRange<Date>) {
    const start = dateRange?.start ? toIsoDateStartOfDay(dateRange.start) : null;
    const end = dateRange?.end ? toIsoDateEndOfDay(dateRange.end) : null;
    const dateRangeStr = {
      fromDate: start,
      toDate: end
    }

    const requestParams = toRequestParams(dateRangeStr);
    return requestParams;
  }

}
